import type {
  SupportAttachment,
  SupportMessage,
  SupportUnreadPreview,
} from './types'
import { normalizeQuickReplies } from './quickReplies'

type Raw = Record<string, unknown>

const str = (v: unknown): string => (v == null ? '' : String(v))
const strOrNull = (v: unknown): string | null => (v == null ? null : String(v))

const normalizeAttachment = (a: Raw): SupportAttachment => ({
  id: str(a.id),
  filename: str(a.filename),
  contentType: str(a.contentType ?? a.content_type),
  size: Number(a.size ?? 0),
  url: str(a.url),
})

export const normalizeMessage = (item: Raw): SupportMessage => ({
  id: str(item.id),
  conversationId: str(item.conversationId ?? item.conversation_id),
  authorType: (item.authorType ?? item.author_type) as SupportMessage['authorType'],
  authorId: (item.authorId ?? item.author_id ?? null) as string | null,
  body: str(item.body),
  createdAt: str(item.createdAt ?? item.created_at),
  attachments: Array.isArray(item.attachments)
    ? (item.attachments as Raw[]).map(normalizeAttachment)
    : [],
  clientId: strOrNull(item.clientId ?? item.client_id),
  agentName: strOrNull(item.agentName ?? item.agent_name),
  actionId: strOrNull(item.actionId ?? item.action_id),
  quickReplies: normalizeQuickReplies(item.quickReplies ?? item.quick_replies),
})

export const sortMessagesAsc = (messages: SupportMessage[]): SupportMessage[] =>
  [...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt))

export const isFromCustomer = (message: SupportMessage): boolean => message.authorType === 'customer'

/** DR-2: unread = non-customer messages newer than the last time the panel was opened. */
export const countUnread = (messages: SupportMessage[], lastSeenIso: string): number =>
  messages.filter((m) => m.authorType !== 'customer' && m.createdAt > lastSeenIso).length

/**
 * DR-1: merge incoming (server) messages into the existing list.
 * - Dedupe by id (incoming wins).
 * - Reconcile the optimistic-send race: drop any local `sending` placeholder whose
 *   clientId matches a confirmed server message (the server id differs from clientId).
 */
export const mergeMessages = (
  existing: SupportMessage[],
  incoming: SupportMessage[]
): SupportMessage[] => {
  const byId = new Map<string, SupportMessage>()
  for (const m of existing) byId.set(m.id, m)
  for (const m of incoming) {
    const prev = byId.get(m.id)
    // Each server fetch mints a FRESH signed attachment URL. Overwriting an on-screen
    // message with a new URL changes the <Image> source and forces a reload (the image
    // visibly disappears, then reappears once the new URL loads). Keep the already-loaded
    // URL for a known attachment (matched by its stable id) so the source stays constant
    // across refetches. Signed URLs are valid ~1h, so the kept one still works.
    if (prev?.attachments?.length) {
      // If a refetch dropped the attachments entirely (transient empty response), keep the
      // ones already on screen so the rendered image never vanishes; otherwise preserve the
      // already-loaded URL per attachment id.
      const atts = (m.attachments?.length ? m.attachments : prev.attachments).map((a) => {
        const kept = prev.attachments.find((p) => p.id === a.id)
        return kept?.url ? { ...a, url: kept.url } : a
      })
      byId.set(m.id, { ...m, attachments: atts })
    } else {
      byId.set(m.id, m)
    }
  }

  const confirmedClientIds = new Set(
    incoming.filter((m) => m.clientId && m.status !== 'sending').map((m) => m.clientId)
  )
  const reconciled = [...byId.values()].filter(
    (m) => !(m.status === 'sending' && m.clientId && confirmedClientIds.has(m.clientId))
  )
  return sortMessagesAsc(reconciled)
}

/** DR-1: replace a `sending` placeholder (keyed by clientId) with its confirmed server message.
 *  Safety-net: if the server attachment url is empty (signed-URL generation lag), backfill
 *  the local blob/object-URL from the matching optimistic attachment (by index, then filename).
 */
export const replaceOptimistic = (
  list: SupportMessage[],
  clientId: string,
  serverMessage: SupportMessage
): SupportMessage[] => {
  const optimistic = list.find((m) => m.clientId === clientId && m.status === 'sending')
  const patched: SupportMessage =
    optimistic && serverMessage.attachments?.length
      ? {
          ...serverMessage,
          attachments: serverMessage.attachments.map((a, i) => {
            if (a.url) return a
            const fallback =
              optimistic.attachments?.[i]?.url ||
              optimistic.attachments?.find((o) => o.filename === a.filename)?.url ||
              ''
            return fallback ? { ...a, url: fallback } : a
          }),
        }
      : serverMessage
  return mergeMessages(
    list.filter((m) => m.clientId !== clientId || m.status !== 'sending'),
    [patched]
  )
}

/**
 * ATL-1343 — read the `latestMessage` of `GET /support/unread`.
 *
 * Returns null for anything that is not a usable preview, including a preview with no `messageId`.
 * That is not defensiveness for its own sake: the toast de-duplicates on that id, so an id-less
 * preview would re-announce the same message on every poll. Null degrades to today's behaviour —
 * the badge still counts — which is also what a bundle running against a backend that predates
 * this field sees.
 */
export const normalizeUnreadPreview = (raw: unknown): SupportUnreadPreview | null => {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Raw
  const messageId = str(r.messageId ?? r.message_id)
  if (!messageId) return null
  return {
    messageId,
    authorType: (r.authorType ?? r.author_type ?? 'ai_agent') as SupportUnreadPreview['authorType'],
    agentName: strOrNull(r.agentName ?? r.agent_name),
    preview: str(r.preview),
    hasAttachments: Boolean(r.hasAttachments ?? r.has_attachments),
    createdAt: strOrNull(r.createdAt ?? r.created_at),
  }
}
